import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import {
  createAction,
  getAction,
  listCustomFields,
  updateAction,
  type ActionPayload,
} from "@/api/actions";
import type { ActionKind, ActionSpec } from "@/api/types";
import { Page } from "@/components/page";

const KINDS: ActionKind[] = ["skip", "extract"];

const EMPTY: ActionPayload = {
  name: "",
  enabled: true,
  spec: { kind: "skip" },
};

export function ActionEditPage() {
  const { actionId } = useParams({ strict: false }) as { actionId: string };
  const isNew = actionId === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const remote = useQuery({
    queryKey: ["action", actionId],
    queryFn: () => getAction(actionId),
    enabled: !isNew,
  });

  const [draft, setDraft] = useState<ActionPayload>(EMPTY);

  useEffect(() => {
    if (!remote.data) return;
    setDraft({
      name: remote.data.name,
      enabled: remote.data.enabled,
      spec: remote.data.spec,
    });
  }, [remote.data]);

  const save = useMutation({
    mutationFn: () =>
      isNew ? createAction(draft) : updateAction(actionId, draft),
    onSuccess: (r) => {
      toast.success(isNew ? "Action created" : "Action saved");
      qc.invalidateQueries({ queryKey: ["actions"] });
      qc.invalidateQueries({ queryKey: ["action", actionId] });
      if (isNew && "id" in r) {
        navigate({
          to: "/actions/$actionId",
          params: { actionId: r.id as string },
        });
      }
    },
  });

  const canSave =
    !!draft.name &&
    (draft.spec.kind === "skip" ||
      (draft.spec.kind === "extract" && !!draft.spec.close_opp_status_id)) &&
    !save.isPending;

  const setKind = (kind: ActionKind) => {
    setDraft((d) => ({
      ...d,
      spec:
        kind === "skip"
          ? { kind: "skip" }
          : { kind: "extract", close_opp_status_id: "", custom_fields: [] },
    }));
  };

  return (
    <Page
      title={isNew ? "New action" : remote.data?.name ?? "Action"}
      actions={
        <>
          <Link to="/actions" className="btn-ghost text-sm">
            <ArrowLeft className="h-4 w-4" /> All actions
          </Link>
          <button
            className="btn-primary"
            onClick={() => save.mutate()}
            disabled={!canSave}
          >
            <Save className="h-4 w-4" /> Save
          </button>
        </>
      }
    >
      <section className="card p-4 space-y-3 max-w-2xl">
        <div>
          <label className="label">Name</label>
          <input
            className="input"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            required
          />
        </div>
        <div>
          <label className="label">Kind</label>
          <select
            className="input"
            value={draft.spec.kind}
            onChange={(e) => setKind(e.target.value as ActionKind)}
          >
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </div>
        <KindConfig spec={draft.spec} onChange={(spec) => setDraft({ ...draft, spec })} />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
          />
          Enabled
        </label>

        <details className="mt-4 text-sm">
          <summary className="cursor-pointer font-semibold">
            What does each kind do?
          </summary>
          <div className="mt-2 space-y-2 leading-relaxed text-muted">
            <p>
              <strong>skip</strong> — domain is tagged but no downstream
              pipeline runs. Use for blocklist-style checks (e.g.{" "}
              <code>bad words</code>).
            </p>
            <p>
              <strong>extract</strong> — fetch the Impressum, push to Close
              as a lead. Each extract action carries its own Close
              opportunity <code>status_id</code> so different domain
              flavours land in different pipelines.
            </p>
          </div>
        </details>
      </section>
    </Page>
  );
}

function KindConfig({
  spec,
  onChange,
}: {
  spec: ActionSpec;
  onChange: (s: ActionSpec) => void;
}) {
  const memo = useMemo(() => spec, [spec]);
  // Hooks must run unconditionally — query the registry before the skip
  // early-return below.
  const fields = useQuery({
    queryKey: ["custom-fields"],
    queryFn: listCustomFields,
  });
  if (memo.kind === "skip") {
    return (
      <p className="text-xs text-muted">No configuration for skip kind.</p>
    );
  }
  const selected = memo.custom_fields ?? [];
  const toggleField = (key: string, checked: boolean) =>
    // Spread `memo` so close_opp_status_id (and any future extract field) is
    // preserved — rebuilding the spec literal here would silently drop it.
    onChange({
      ...memo,
      custom_fields: checked
        ? [...selected.filter((k) => k !== key), key]
        : selected.filter((k) => k !== key),
    });
  return (
    <div className="space-y-3">
      <div>
        <label className="label">Close opportunity status_id</label>
        <input
          className="input font-mono"
          value={memo.close_opp_status_id}
          onChange={(e) =>
            onChange({ ...memo, close_opp_status_id: e.target.value })
          }
          placeholder="stat_…"
        />
        <p className="mt-1 text-xs text-muted">
          Close opportunity-pipeline status_id this action routes to. Look up
          the value in Close (Settings → Pipelines).
        </p>
      </div>
      <div>
        <label className="label">Close custom fields</label>
        <p className="mb-1 text-xs text-muted">
          Which custom fields this action writes on the lead.
        </p>
        <div className="space-y-1">
          {(fields.data ?? []).map((f) => (
            <label key={f.key} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={selected.includes(f.key)}
                onChange={(e) => toggleField(f.key, e.target.checked)}
              />
              {f.label}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
